import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_THEME = 'colosseum_theme';
const STORAGE_MODE  = 'colosseum_theme_mode'; // 'manual' | 'auto'

/**
 * THEMES — single source of truth. Adding a new theme means:
 *   1. Add an entry here
 *   2. Add a `[data-theme="<key>"]` block in index.css
 *   3. (Optional) Add it to the admin allow-list default in server.py
 */
export const THEMES = [
  {
    key: 'charcoal',
    label: 'Charcoal',
    blurb: 'The original — dark stone, parchment text, gold.',
    swatch: ['#1C1917', '#D4AF37', '#F4EFE6'],
  },
  {
    key: 'parchment',
    label: 'Parchment',
    blurb: 'Warm cream, dark ink, soft gold. Daylight read.',
    swatch: ['#FAF5E8', '#9F7B2C', '#2A1A10'],
  },
  {
    key: 'modern-art',
    label: 'Classic Purple',
    blurb: 'Sultry midnight violet, gold sparkle. Owen Jun 2026 round 30 rename — was "Modern Art".',
    swatch: ['#2D1955', '#D4AF37', '#F4EFE6'],
  },
  {
    key: 'royal-blue',
    label: 'Royal Blue',
    blurb: 'Gold-flecked midnight blue — the Family Legacy Scroll palette, now app-wide.',
    swatch: ['#0E1A3A', '#D4AF37', '#F4EFE6'],
  },
  {
    key: 'klimt',
    label: 'Klimt',
    blurb: 'Midnight teal, antique gold, ivory. Ornate.',
    swatch: ['#0F2A2E', '#C9A961', '#F4E8D0'],
  },
  {
    key: 'slate',
    label: 'Slate',
    blurb: 'Warm graphite walls, antique gold, cream. Gallery hush.',
    swatch: ['#44413D', '#D4AF37', '#F4EFE6'],
  },
];

/**
 * Mood-of-the-day rotation. Each day gets exactly one theme so the app
 * develops a quiet weekly rhythm without ever feeling random. Manual
 * picks always override this — see ThemeProvider for the mode logic.
 *
 * 0 = Sun, 1 = Mon, ..., 6 = Sat.
 */
const MOOD_ROTATION = {
  0: 'slate',        // Sunday — reflective, sophisticated hush
  1: 'charcoal',     // Monday — back to work
  2: 'charcoal',     // Tuesday
  3: 'klimt',        // Wednesday — mid-week lift
  4: 'charcoal',     // Thursday
  5: 'modern-art',   // Friday — energy
  6: 'parchment',    // Saturday — Sabbath daylight read
};

export const moodOfTheDay = (date = new Date()) => MOOD_ROTATION[date.getDay()] || 'charcoal';

const ThemeContext = createContext({
  theme: 'charcoal',
  mode: 'manual',
  setTheme: () => {},
  setMode: () => {},
  available: THEMES.map((t) => t.key),
  loading: false,
});

export const useTheme = () => useContext(ThemeContext);

const applyTheme = (key) => {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (!key || key === 'charcoal') {
    html.removeAttribute('data-theme');
  } else {
    html.setAttribute('data-theme', key);
  }
};

const readStored = (k, fallback) => {
  try { return localStorage.getItem(k) || fallback; } catch (e) { return fallback; }
};
const writeStored = (k, v) => {
  try { localStorage.setItem(k, v); } catch (e) { /* ignore */ }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    // Migration (Jun 2026): Watercolor was replaced with Slate. Anyone on
    // the old theme is silently bumped to Slate so nothing breaks.
    const stored = readStored(STORAGE_THEME, 'charcoal');
    if (stored === 'watercolor') {
      try { localStorage.setItem(STORAGE_THEME, 'slate'); } catch { /* ignore */ }
      return 'slate';
    }
    return stored;
  });
  const [mode, setModeState]   = useState(() => readStored(STORAGE_MODE, 'manual'));
  const [available, setAvailable] = useState(THEMES.map((t) => t.key));
  const [loading, setLoading] = useState(true);
  // Tracks whether we've successfully hydrated from the backend yet —
  // we want to AVOID syncing the user's first localStorage value back
  // to the server before we've heard what the server thinks.
  const hydrated = useRef(false);

  // Resolve which theme to apply based on mode. Manual = stored theme.
  // Auto = mood-of-the-day computed fresh on every mount.
  useEffect(() => {
    const effective = mode === 'auto' ? moodOfTheDay() : theme;
    applyTheme(effective);
  }, [theme, mode]);

  // Hydrate admin allow-list + user's saved theme/mode from backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/themes/available`);
        if (cancelled) return;
        const list = Array.isArray(r.data?.themes) ? r.data.themes : null;
        if (list && list.length > 0) {
          setAvailable(list);
          if (!list.includes(theme)) {
            setThemeState('charcoal');
            writeStored(STORAGE_THEME, 'charcoal');
          }
        }
      } catch (e) { /* keep client defaults */ }

      // Best-effort: hydrate from server (only succeeds if authenticated).
      try {
        const r = await axios.get(`${API}/me/theme-preference`, { withCredentials: true });
        if (cancelled) return;
        const srvTheme = r.data?.theme;
        const srvMode  = r.data?.mode;
        // Migration: legacy 'watercolor' value coming back from the server
        // (Jun 2026 theme replacement) is rewritten to 'slate'.
        const migratedTheme = srvTheme === 'watercolor' ? 'slate' : srvTheme;
        if (migratedTheme && THEMES.find((t) => t.key === migratedTheme)) {
          setThemeState(migratedTheme);
          writeStored(STORAGE_THEME, migratedTheme);
          // Best-effort: tell the server to forget the old value.
          if (srvTheme === 'watercolor') {
            axios.patch(`${API}/me/theme-preference`, { theme: 'slate' }, { withCredentials: true }).catch(() => { /* ignore */ });
          }
        }
        if (srvMode === 'manual' || srvMode === 'auto') {
          setModeState(srvMode);
          writeStored(STORAGE_MODE, srvMode);
        }
      } catch (e) { /* anonymous — that's fine */ }

      if (!cancelled) {
        hydrated.current = true;
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push theme/mode changes to backend after hydration (best-effort,
  // silent on 401 = anonymous).
  const pushToServer = useCallback((nextTheme, nextMode) => {
    if (!hydrated.current) return;
    const body = {};
    if (nextTheme !== undefined) body.theme = nextTheme;
    if (nextMode  !== undefined) body.mode  = nextMode;
    if (Object.keys(body).length === 0) return;
    axios.patch(`${API}/me/theme-preference`, body, { withCredentials: true }).catch(() => { /* ignore */ });
  }, []);

  // Picking a theme always switches mode to manual — Owen's brief:
  // "manual choice always wins" (Jun 2026).
  const setTheme = useCallback((key) => {
    if (!THEMES.find((t) => t.key === key)) return;
    setThemeState(key);
    writeStored(STORAGE_THEME, key);
    setModeState('manual');
    writeStored(STORAGE_MODE, 'manual');
    pushToServer(key, 'manual');
  }, [pushToServer]);

  const setMode = useCallback((nextMode) => {
    if (nextMode !== 'manual' && nextMode !== 'auto') return;
    setModeState(nextMode);
    writeStored(STORAGE_MODE, nextMode);
    pushToServer(undefined, nextMode);
  }, [pushToServer]);

  return (
    <ThemeContext.Provider value={{ theme, mode, setTheme, setMode, available, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};
